/**
 * Real results video slider — infinite center loop.
 * Play only on play-button click (no autoplay). Sound toggle top-right.
 * Tag: <zoh-real-results>
 */

class ZohRealResults extends HTMLElement {
  /** @type {HTMLElement | null} */
  #viewport = null;

  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLButtonElement | null} */
  #prev = null;

  /** @type {HTMLButtonElement | null} */
  #next = null;

  /** @type {HTMLElement[]} */
  #slides = [];

  /** @type {number} */
  #realCount = 0;

  /** @type {number} */
  #index = 0;

  /** @type {boolean} */
  #isAnimating = false;

  /** @type {IntersectionObserver | null} */
  #sectionObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number | null} */
  #rafId = null;

  /** @type {number | null} */
  #animTimeout = null;

  /** @type {boolean} */
  #pendingPlay = false;

  connectedCallback() {
    this.#viewport = this.querySelector('[data-zoh-rr-viewport]');
    this.#track = this.querySelector('[data-zoh-rr-track]');
    this.#prev = this.querySelector('[data-zoh-rr-prev]');
    this.#next = this.querySelector('[data-zoh-rr-next]');

    if (!this.#viewport || !this.#track) return;

    this.#buildInfiniteSlides();
    this.#slides = Array.from(this.#track.querySelectorAll('[data-zoh-rr-slide]'));

    if (this.#realCount === 0) return;

    this.#index = this.#realCount;
    this.#bindEvents();
    this.#observeSection();
    this.#prepareVideos();
    this.#goTo(this.#index, false);
    this.#scheduleUpdate();

    window.addEventListener('load', this.#onResize, { passive: true });
  }

  disconnectedCallback() {
    this.#prev?.removeEventListener('click', this.#onPrev);
    this.#next?.removeEventListener('click', this.#onNext);
    this.#track?.removeEventListener('transitionend', this.#onTransitionEnd);
    this.#track?.removeEventListener('click', this.#onTrackClick);
    this.removeEventListener('keydown', this.#onKeydown);
    window.removeEventListener('resize', this.#onResize);
    window.removeEventListener('load', this.#onResize);

    this.#sectionObserver?.disconnect();
    this.#sectionObserver = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }

    if (this.#animTimeout !== null) {
      window.clearTimeout(this.#animTimeout);
      this.#animTimeout = null;
    }

    this.#pauseAll();
  }

  #buildInfiniteSlides() {
    if (!this.#track) return;

    const originals = Array.from(this.#track.querySelectorAll('[data-zoh-rr-slide]'));
    this.#realCount = originals.length;

    if (this.#realCount <= 1) return;

    const prependFrag = document.createDocumentFragment();
    const appendFrag = document.createDocumentFragment();

    originals.forEach((slide) => {
      const before = /** @type {HTMLElement} */ (slide.cloneNode(true));
      const after = /** @type {HTMLElement} */ (slide.cloneNode(true));
      before.setAttribute('data-clone', 'true');
      after.setAttribute('data-clone', 'true');
      before.setAttribute('aria-hidden', 'true');
      after.setAttribute('aria-hidden', 'true');
      before.setAttribute('inert', '');
      after.setAttribute('inert', '');
      prependFrag.appendChild(before);
      appendFrag.appendChild(after);
    });

    this.#track.insertBefore(prependFrag, originals[0]);
    this.#track.appendChild(appendFrag);
  }

  #bindEvents() {
    this.#prev?.addEventListener('click', this.#onPrev);
    this.#next?.addEventListener('click', this.#onNext);
    this.#track?.addEventListener('transitionend', this.#onTransitionEnd);
    this.#track?.addEventListener('click', this.#onTrackClick);
    this.addEventListener('keydown', this.#onKeydown);
    window.addEventListener('resize', this.#onResize, { passive: true });

    if (typeof ResizeObserver !== 'undefined' && this.#viewport) {
      this.#resizeObserver = new ResizeObserver(() => this.#scheduleUpdate());
      this.#resizeObserver.observe(this.#viewport);
      this.#resizeObserver.observe(this);
    }
  }

  #observeSection() {
    this.#sectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        if (!entry.isIntersecting) {
          this.#pauseAll();
        }
      },
      { threshold: 0.35 }
    );

    this.#sectionObserver.observe(this);
  }

  #prepareVideos() {
    this.#slides.forEach((slide) => {
      const video = slide.querySelector('video');
      if (video instanceof HTMLVideoElement) {
        this.#deferVideo(video);
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('muted', '');

        video.addEventListener('play', () => this.#setPlaying(slide, true));
        video.addEventListener('pause', () => {
          slide.removeAttribute('data-loading');
          this.#setPlaying(slide, false);
        });
        video.addEventListener('ended', () => {
          slide.removeAttribute('data-loading');
          this.#setPlaying(slide, false);
        });
        video.addEventListener('waiting', () => slide.setAttribute('data-loading', ''));
        video.addEventListener('stalled', () => slide.setAttribute('data-loading', ''));
        video.addEventListener('playing', () => slide.removeAttribute('data-loading'));
        video.addEventListener('canplay', () => {
          if (!video.paused) slide.removeAttribute('data-loading');
        });
        video.addEventListener('canplaythrough', () => {
          if (!video.paused) slide.removeAttribute('data-loading');
        });
      }

      const iframe = slide.querySelector('iframe[data-zoh-rr-embed]');
      if (iframe instanceof HTMLIFrameElement) {
        this.#deferEmbed(iframe);
      }
    });
  }

  /**
   * Strip media URLs so nothing downloads until needed.
   * @param {HTMLVideoElement} video
   */
  #deferVideo(video) {
    if (video.dataset.zohRrDeferred === 'true') return;

    /** @type {Array<{ src: string, type: string }>} */
    const sources = [];

    video.querySelectorAll('source').forEach((source) => {
      const src = source.getAttribute('src');
      if (!src) return;
      sources.push({
        src,
        type: source.getAttribute('type') || '',
      });
      source.remove();
    });

    const directSrc = video.getAttribute('src');
    if (directSrc) {
      sources.push({ src: directSrc, type: '' });
      video.removeAttribute('src');
    }

    video.dataset.zohRrSources = JSON.stringify(sources);
    video.dataset.zohRrDeferred = 'true';
    video.dataset.zohRrAttached = 'false';
    video.preload = 'none';

    try {
      video.load();
    } catch {
      // ignore
    }
  }

  /**
   * @param {HTMLIFrameElement} iframe
   */
  #deferEmbed(iframe) {
    if (iframe.dataset.zohRrDeferred === 'true') return;

    const src = iframe.getAttribute('src');
    if (src && src !== 'about:blank') {
      iframe.dataset.embedSrc = src;
    }

    iframe.removeAttribute('src');
    iframe.dataset.zohRrDeferred = 'true';
    iframe.dataset.zohRrAttached = 'false';
  }

  /**
   * @param {HTMLVideoElement} video
   * @param {'none' | 'metadata' | 'auto'} preload
   * @returns {Promise<HTMLVideoElement>}
   */
  #attachVideoSources(video, preload = 'auto') {
    if (video.dataset.zohRrAttached === 'true') {
      return Promise.resolve(video);
    }

    /** @type {Array<{ src: string, type: string }>} */
    let sources = [];
    try {
      sources = JSON.parse(video.dataset.zohRrSources || '[]');
    } catch {
      sources = [];
    }

    if (sources.length === 0) {
      video.dataset.zohRrAttached = 'true';
      return Promise.resolve(video);
    }

    sources.forEach((item) => {
      if (!item.src) return;
      const source = document.createElement('source');
      source.src = item.src;
      if (item.type) source.type = item.type;
      video.appendChild(source);
    });

    video.preload = preload;
    video.dataset.zohRrAttached = 'true';

    try {
      video.load();
    } catch {
      // ignore
    }

    return new Promise((resolve) => {
      if (video.readyState >= 2) {
        resolve(video);
        return;
      }

      const done = () => {
        video.removeEventListener('loadeddata', done);
        video.removeEventListener('canplay', done);
        window.clearTimeout(timeoutId);
        resolve(video);
      };

      const timeoutId = window.setTimeout(done, 10000);
      video.addEventListener('loadeddata', done, { once: true });
      video.addEventListener('canplay', done, { once: true });
    });
  }

  /**
   * @param {HTMLIFrameElement} iframe
   * @returns {Promise<HTMLIFrameElement>}
   */
  #attachEmbed(iframe) {
    if (iframe.dataset.zohRrAttached === 'true' && iframe.getAttribute('src')) {
      return Promise.resolve(iframe);
    }

    const src = iframe.dataset.embedSrc;
    if (!src) {
      iframe.dataset.zohRrAttached = 'true';
      return Promise.resolve(iframe);
    }

    return new Promise((resolve) => {
      const done = () => {
        window.clearTimeout(timeoutId);
        resolve(iframe);
      };

      const timeoutId = window.setTimeout(done, 8000);
      iframe.addEventListener('load', done, { once: true });
      iframe.src = src;
      iframe.dataset.zohRrAttached = 'true';
    });
  }

  /** Prefetch metadata for the centered slide only (not full download). */
  #warmActiveSlide() {
    const active = this.#activeSlide();
    if (!active) return;

    const video = active.querySelector('video');
    if (video instanceof HTMLVideoElement) {
      this.#attachVideoSources(video, 'metadata');
    }
  }

  /** @returns {number} */
  #getGap() {
    if (!this.#track) return 0;
    const styles = getComputedStyle(this.#track);
    const gap = Number.parseFloat(styles.gap || styles.columnGap || '0');
    return Number.isNaN(gap) ? 0 : gap;
  }

  /**
   * @param {number} index
   * @param {boolean} animate
   * @param {{ pause?: boolean }} [options]
   */
  #goTo(index, animate = true, options = {}) {
    const pause = options.pause !== false;

    if (!this.#track || !this.#viewport || this.#slides.length === 0) return;

    const slide = this.#slides[index];
    if (!(slide instanceof HTMLElement)) return;

    const viewportW = this.#viewport.clientWidth;
    const slideW = slide.offsetWidth;
    const gap = this.#getGap();
    const slideCenter = index * (slideW + gap) + slideW / 2;
    const offset = viewportW / 2 - slideCenter;

    this.#track.style.transition = animate ? '' : 'none';
    this.#track.style.transform = `translate3d(${offset}px, 0, 0)`;
    this.#index = index;

    this.#slides.forEach((node, i) => {
      const isActive = i === index;
      node.toggleAttribute('data-active', isActive);
      if (node.hasAttribute('data-clone')) {
        node.setAttribute('aria-hidden', 'true');
        node.setAttribute('inert', '');
      } else {
        node.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        if (isActive) {
          node.removeAttribute('inert');
        } else {
          node.setAttribute('inert', '');
        }
      }
    });

    if (!animate) {
      void this.#track.offsetWidth;
      this.#track.style.transition = '';
    } else if (this.#realCount > 1) {
      if (this.#animTimeout !== null) window.clearTimeout(this.#animTimeout);
      this.#animTimeout = window.setTimeout(() => {
        this.#animTimeout = null;
        this.#finishAnimation();
      }, 500);
    }

    if (pause) {
      this.#pauseAll();
    }

    this.#warmActiveSlide();
  }

  #normalizeIndex() {
    if (this.#realCount <= 1) return;

    if (this.#index < this.#realCount) {
      this.#goTo(this.#index + this.#realCount, false, { pause: false });
    } else if (this.#index >= this.#realCount * 2) {
      this.#goTo(this.#index - this.#realCount, false, { pause: false });
    }
  }

  #finishAnimation() {
    if (!this.#isAnimating) return;
    this.#isAnimating = false;
    if (this.#animTimeout !== null) {
      window.clearTimeout(this.#animTimeout);
      this.#animTimeout = null;
    }
    this.#normalizeIndex();

    if (this.#pendingPlay) {
      this.#pendingPlay = false;
      const active = this.#activeSlide();
      if (active) this.#playSlide(active);
    }
  }

  #onTransitionEnd = (event) => {
    if (event.target !== this.#track) return;
    if (event.propertyName !== 'transform') return;
    this.#finishAnimation();
  };

  #onPrev = () => {
    if (this.#isAnimating || this.#realCount <= 1) return;
    this.#isAnimating = true;
    this.#goTo(this.#index - 1, true);
  };

  #onNext = () => {
    if (this.#isAnimating || this.#realCount <= 1) return;
    this.#isAnimating = true;
    this.#goTo(this.#index + 1, true);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (this.#realCount <= 1) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#onPrev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#onNext();
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #onTrackClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const soundBtn = target.closest('[data-zoh-rr-sound]');
    if (soundBtn instanceof HTMLElement && this.contains(soundBtn)) {
      event.preventDefault();
      event.stopPropagation();
      const soundSlide = soundBtn.closest('[data-zoh-rr-slide]');
      if (soundSlide instanceof HTMLElement) {
        this.#toggleSound(soundSlide);
      }
      return;
    }

    const playBtn = target.closest('[data-zoh-rr-play]');
    const slide = target.closest('[data-zoh-rr-slide]');

    if (playBtn instanceof HTMLElement && slide instanceof HTMLElement) {
      event.preventDefault();
      const slideIndex = this.#slides.indexOf(slide);
      if (slideIndex < 0) return;

      if (slideIndex !== this.#index) {
        if (this.#isAnimating) return;
        this.#pendingPlay = true;
        if (this.#realCount > 1) {
          this.#isAnimating = true;
          this.#goTo(slideIndex, true);
        } else {
          this.#goTo(slideIndex, false);
          this.#pendingPlay = false;
          const active = this.#activeSlide();
          if (active) this.#playSlide(active);
        }
        return;
      }

      if (slide.hasAttribute('data-playing')) {
        this.#pauseSlide(slide);
      } else {
        this.#playSlide(slide);
      }
      return;
    }

    if (slide instanceof HTMLElement && slide.hasAttribute('data-active') && slide.hasAttribute('data-playing')) {
      event.preventDefault();
      this.#pauseSlide(slide);
      return;
    }

    if (slide instanceof HTMLElement && !slide.hasAttribute('data-active')) {
      const slideIndex = this.#slides.indexOf(slide);
      if (slideIndex < 0 || this.#isAnimating) return;
      this.#isAnimating = this.#realCount > 1;
      this.#goTo(slideIndex, this.#realCount > 1);
    }
  };

  #onResize = () => {
    this.#scheduleUpdate();
  };

  #scheduleUpdate() {
    if (this.#rafId !== null) cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#goTo(this.#index, false, { pause: false });
    });
  }

  /** @returns {HTMLElement | null} */
  #activeSlide() {
    return this.#slides[this.#index] ?? null;
  }

  /**
   * @param {HTMLElement} slide
   */
  #toggleSound(slide) {
    const turningOn = !slide.hasAttribute('data-sound-on');

    if (turningOn) {
      // Only this video may be unmuted
      this.#slides.forEach((node) => {
        if (node === slide) return;
        node.removeAttribute('data-sound-on');
        this.#syncSoundButton(node);
        this.#setSlideMuted(node, true);
      });
      slide.setAttribute('data-sound-on', '');
    } else {
      slide.removeAttribute('data-sound-on');
    }

    this.#syncSoundButton(slide);
    this.#setSlideMuted(slide, !turningOn);
  }

  /**
   * @param {HTMLElement} slide
   */
  #syncSoundButton(slide) {
    const btn = slide.querySelector('[data-zoh-rr-sound]');
    if (!(btn instanceof HTMLElement)) return;

    const soundOn = slide.hasAttribute('data-sound-on');
    btn.toggleAttribute('data-sound-on', soundOn);
    btn.setAttribute('aria-pressed', soundOn ? 'true' : 'false');
    const labelOn = btn.getAttribute('data-label-on') || 'Mute sound';
    const labelOff = btn.getAttribute('data-label-off') || 'Unmute sound';
    btn.setAttribute('aria-label', soundOn ? labelOn : labelOff);
  }

  /**
   * @param {HTMLElement} slide
   * @param {boolean} muted
   */
  #setSlideMuted(slide, muted) {
    const video = slide.querySelector('video');
    if (video instanceof HTMLVideoElement) {
      video.muted = muted;
      if (muted) {
        video.setAttribute('muted', '');
      } else {
        video.removeAttribute('muted');
      }
      return;
    }

    const iframe = slide.querySelector('iframe[data-zoh-rr-embed]');
    if (!(iframe instanceof HTMLIFrameElement)) return;

    const type = iframe.getAttribute('data-video-type');
    if (type === 'youtube') {
      iframe.contentWindow?.postMessage(
        JSON.stringify({
          event: 'command',
          func: muted ? 'mute' : 'unMute',
          args: '',
        }),
        '*'
      );
    } else if (type === 'vimeo') {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ method: 'setVolume', value: muted ? 0 : 1 }),
        '*'
      );
    }
  }

  #pauseAll() {
    this.#slides.forEach((slide) => {
      this.#pauseSlide(slide);
    });
  }

  /**
   * @param {HTMLElement} slide
   */
  #playSlide(slide) {
    this.#slides.forEach((node) => {
      if (node === slide) return;
      this.#pauseSlide(node);
    });

    this.#setSlideMuted(slide, !slide.hasAttribute('data-sound-on'));
    this.#playMedia(slide);
  }

  /**
   * @param {HTMLElement} slide
   */
  #pauseSlide(slide) {
    slide.removeAttribute('data-loading');
    this.#setPlaying(slide, false);
    this.#pauseMedia(slide);
  }

  /**
   * @param {HTMLElement} slide
   * @param {boolean} playing
   */
  #setPlaying(slide, playing) {
    slide.toggleAttribute('data-playing', playing);
    const btn = slide.querySelector('[data-zoh-rr-play]');
    if (btn instanceof HTMLElement) {
      btn.removeAttribute('aria-hidden');
      btn.tabIndex = 0;
      const labelPlay = btn.getAttribute('data-label-play') || 'Play video';
      const labelPause = btn.getAttribute('data-label-pause') || 'Pause video';
      btn.setAttribute('aria-label', playing ? labelPause : labelPlay);
    }
  }

  /**
   * @param {HTMLElement} slide
   */
  #playMedia(slide) {
    const soundOn = slide.hasAttribute('data-sound-on');
    const video = slide.querySelector('video');

    if (video instanceof HTMLVideoElement) {
      slide.setAttribute('data-loading', '');

      this.#attachVideoSources(video, 'auto')
        .then(() => {
          this.#setSlideMuted(slide, !soundOn);
          const playPromise = video.play();
          if (playPromise !== undefined) {
            return playPromise;
          }
          return undefined;
        })
        .then(() => {
          this.#setPlaying(slide, true);
          slide.removeAttribute('data-loading');
        })
        .catch(() => {
          this.#setPlaying(slide, false);
          slide.removeAttribute('data-loading');
        });
      return;
    }

    const iframe = slide.querySelector('iframe[data-zoh-rr-embed]');
    if (!(iframe instanceof HTMLIFrameElement)) return;

    slide.setAttribute('data-loading', '');

    this.#attachEmbed(iframe)
      .then(() => new Promise((resolve) => window.setTimeout(resolve, 350)))
      .then(() => {
        const type = iframe.getAttribute('data-video-type');
        if (type === 'youtube') {
          iframe.contentWindow?.postMessage(
            JSON.stringify({ event: 'command', func: 'playVideo', args: '' }),
            '*'
          );
          iframe.contentWindow?.postMessage(
            JSON.stringify({
              event: 'command',
              func: soundOn ? 'unMute' : 'mute',
              args: '',
            }),
            '*'
          );
        } else if (type === 'vimeo') {
          iframe.contentWindow?.postMessage(JSON.stringify({ method: 'play' }), '*');
          iframe.contentWindow?.postMessage(
            JSON.stringify({ method: 'setVolume', value: soundOn ? 1 : 0 }),
            '*'
          );
        }

        this.#setPlaying(slide, true);
        slide.removeAttribute('data-loading');
      })
      .catch(() => {
        this.#setPlaying(slide, false);
        slide.removeAttribute('data-loading');
      });
  }

  /**
   * @param {HTMLElement} slide
   */
  #pauseMedia(slide) {
    const video = slide.querySelector('video');
    if (video instanceof HTMLVideoElement) {
      video.pause();
      return;
    }

    const iframe = slide.querySelector('iframe[data-zoh-rr-embed]');
    if (!(iframe instanceof HTMLIFrameElement)) return;

    // Embed not attached yet — nothing to pause
    if (iframe.dataset.zohRrAttached !== 'true') return;

    const type = iframe.getAttribute('data-video-type');
    if (type === 'youtube') {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }),
        '*'
      );
    } else if (type === 'vimeo') {
      iframe.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
    }
  }
}

if (!customElements.get('zoh-real-results')) {
  customElements.define('zoh-real-results', ZohRealResults);
}
